import {Icon} from '@atoms';

import {getIsUserAllowedToReserveParking, getLicensePlates, getParkingSettings, ParkingReservation} from '@lib/store';
import {OfficeWorkday} from '@lib/store/src/data/timeslots/apiTypes';
import {trackEvent} from '@lib/infrastructure';
import {useAppSelector, useModal} from '@hooks';
import {ParkingSelectionCard} from 'src/components/organisms/parking';
import {LicensePlatesModalPage} from 'src/components/organisms/profile/licensePlates/LicensePlatesModalPage/LicensePlatesModalPage';
import {useMemoDate} from '@lib/store/src/ui/hooks/useMemoDate';

interface Props {
  workday: OfficeWorkday;
  parkingReservation: ParkingReservation;
  selectedDate: string;
}

export const ParkingReservationTile = ({workday, parkingReservation, selectedDate}: Props) => {
  const {addPages, openModal} = useModal();
  const buildingId = workday.buildingId;
  const date = useMemoDate(selectedDate);

  const isUserAllowedToReserveParking = useAppSelector((state) =>
    getIsUserAllowedToReserveParking(state, date, buildingId),
  );

  const parkingSettings = useAppSelector(getParkingSettings);

  const isLicensePlateMandatory = parkingSettings?.licensePlateIsRequired ?? false;
  const licensePlates = useAppSelector(getLicensePlates);
  const isDisabled = !isUserAllowedToReserveParking && !parkingReservation;

  const shouldOpenLicensePlateModal = isLicensePlateMandatory && !licensePlates?.length;

  return (
    <button
      onClick={() => {
        if (isDisabled) return;

        trackEvent('StartParkingReservationInDayOverview');

        if (shouldOpenLicensePlateModal) {
          addPages([
            <LicensePlatesModalPage
              date={selectedDate}
              buildingId={buildingId}
            />,
          ]);
        } else {
          addPages([
            <ParkingSelectionCard
              date={selectedDate}
              buildingId={buildingId}
            />,
          ]);
        }

        openModal();
      }}
      className="flex flex-row gap-2 w-full justify-center items-center border-t py-5 px-5">
      <Icon
        icon="car"
        size="1.5rem"
      />

      <div className="flex flex-col justify-center min-h-8 ml-4">
        <p className="text-[15px] font-semibold text-black">{parkingReservation.parkingLotName}</p>
        {parkingReservation.zoneName !== null && <p className="text-base text-black">{parkingReservation.zoneName}</p>}
      </div>

      <div className="ml-auto self-center">
        <Icon
          icon="caretRight"
          size="1.3rem"
        />
      </div>
    </button>
  );
};
